import type { FC, SVGProps } from "react"
import { useCallback, useMemo, useState } from "react"
import { useQuery } from "@rocicorp/zero/react"
import { useNavigate, useParams } from "@tanstack/react-router"
import {
	IconArrowsExpandDiagonal4Outline18 as ExpandIcon,
	IconMoveToRightOutline18 as MoveToPortalIcon,
	IconXmarkOutline18 as XIcon,
} from "nucleo-ui-outline-18"

import type { Project } from "@productlane/zero"
import {
	Button,
	Empty,
	IconButton,
	LinearIcon,
	Tabs,
	TabsContent,
	TabsList,
	TabsTrigger,
} from "@productlane/ui"
import {
	DropdownMenu,
	DropdownMenuGroup,
	DropdownMenuItem,
} from "@productlane/ui/src/components/internal/dropdown"
import { EllipsisVerticalIcon as MoreVertical } from "@productlane/ui/src/icons/project/ellipsis-icon"
import { mutators } from "@productlane/zero/mutators"
import { queries } from "@productlane/zero/queries"

import EmptyIssue from "@/assets/empty-states/empty-issue.png"
import {
	DetailScreenEditor,
	DetailScreenLayout,
	DetailThreadsList,
	hasActiveDetailFilters,
	useDetailThreadsControls,
} from "@/components/detail-screen"
import { DetailThreadsToolbar } from "@/components/detail-screen/detail-threads-toolbar"
import { DetailUpvotesList } from "@/components/detail-screen/detail-upvotes-list"
import { countUpvotes } from "@/components/detail-screen/upvote-count"
import { useDetailListState } from "@/components/detail-screen/use-detail-list-state"
import { ProjectOrIssueListItem } from "@/components/issue-or-project-items/project-or-issue-list-item"
import { MetaInfo } from "@/components/meta-info"
import { SubscribeButton } from "@/components/notifications/subscribe-button"
import { RoleCheck } from "@/components/shared/role-check"
import { toast } from "@/components/toast"
import { useShortcut } from "@/lib"
import { analytics } from "@/lib/analytics"
import {
	useBackNavigation,
	usePreviousNavEntry,
	useSetBackNavigation,
} from "@/lib/back-navigation"
import { detailPath } from "@/lib/detail-drawer"
import { generateId } from "@/lib/id"
import { getProjectIcon } from "@/lib/projects"
import { isQuerySettled } from "@/lib/query-settled"
import { useFilteredIssues } from "@/lib/use-team-filter"
import { reportServerRejection, useZero, useZeroWorkspace } from "@/lib/zero"
import { MoveRequestToPortalDialog } from "@/screens/app/roadmap/move-requests-to-portal-dialog"
import {
	getLanguageByCodeWithFallback,
	LanguageSelector,
	useSelectedLanguage,
} from "@/screens/settings/settings-pages/language-selector"
import { HowItWorksButton } from "@/screens/settings/settings-switches/how-it-works-button"
import {
	DescriptionSwitch,
	TitleTabsSwitcher,
	useTitleTab,
} from "../../../components/shared/description-switch"
import { NotifyRecipients } from "../../../components/shared/notify-recipients/index"
import { showsPortalVisibility } from "../../../components/shared/visibility-labels"
import { VisibilityToggle } from "../../../components/shared/visibility-toggle"
import { SelectLinearIssuesDialog } from "../changelog/select-linear-issues-dialog"
import { BreadcrumbSlash, DetailBreadcrumb, DetailTopBar } from "../page-header"
import { ImportIssuesButton } from "./import-issues-button"
import { UpdateProjectDialog } from "./update-project-dialog"

type DetailsTabsType = "issues" | "requests" | "upvotes"

export const ProjectDetailScreen = () => {
	const params = useParams({ strict: false })
	const id = params.id!
	useSetBackNavigation({
		type: "projects-detail",
		url: `/projects/${id}`,
	})

	return <ProjectDetailView id={id} />
}

interface ProjectDetailViewProps {
	id: string
	variant?: "page" | "drawer"
	onClose?: () => void
}

export const ProjectDetailView = ({
	id,
	variant = "page",
	onClose,
}: ProjectDetailViewProps) => {
	const isDrawer = variant === "drawer"
	const zero = useZero()
	const workspace = useZeroWorkspace()
	const [selectedLanguage, setSelectedLanguage] = useSelectedLanguage()
	// Translations only matter on a published portal; otherwise edit the default language.
	const editorLanguage = workspace?.domainPublic
		? selectedLanguage
		: getLanguageByCodeWithFallback(workspace?.defaultLanguage)
	const hasPublicFeatureRequests = !!workspace?.domainPublic
	const [titleTab, setTitleTab] = useTitleTab(hasPublicFeatureRequests)
	const [isIssuesDialogOpen, setIsIssuesDialogOpen] = useState(false)
	const [isMoveToPortalOpen, setIsMoveToPortalOpen] = useState(false)

	const [project, projectResult] = useQuery(queries.projectDetail(id ?? ""))

	// Filter issues by selected teams
	const filteredProjectIssues = useFilteredIssues(project?.issues ?? [])

	const sortedProjectIssues = useMemo(() => {
		if (!filteredProjectIssues.length) return []
		return [...filteredProjectIssues].sort(
			(a, b) => b.totalScore - a.totalScore,
		)
	}, [filteredProjectIssues])

	const issuesMaxPainLevel = useMemo(() => {
		if (!sortedProjectIssues.length) return 0
		return Math.max(...sortedProjectIssues.map((i) => i.totalScore))
	}, [sortedProjectIssues])

	const threadsState = useDetailListState()

	const threadControls = useDetailThreadsControls()
	const [teamMembers] = useQuery(queries.teamMembers())

	const navigate = useNavigate()

	const [updateProject, setUpdateProject] = useState<Project | null>(null)

	const { goBack } = useBackNavigation()
	const previousEntry = usePreviousNavEntry("projects-detail")
	const close = useCallback(
		(shortcut = false) => {
			if (!project) return
			analytics.track("project-close", { id: project.id, shortcut })
			if (isDrawer) {
				onClose?.()
				return
			}
			goBack(`/projects${project.state === "completed" ? "?state=closed" : ""}`)
		},
		[goBack, isDrawer, onClose, project],
	)
	// The drawer is a Radix dialog, so Escape is already wired to onClose there.
	useShortcut(
		"esc",
		() => {
			close(true)
		},
		{ enabled: !isDrawer },
	)

	useShortcut(
		"l",
		() => {
			project?.url && window.open(project.url, "_blank")
		},
		{ preventDefault: true },
	)

	const [legacyVoters] = useQuery(queries.projectUpvoters({ projectId: id }))
	const upvoteCount = countUpvotes(project?.feedbacks ?? [], legacyVoters)

	const [selectedTab, setTab] = useState<DetailsTabsType>("requests")
	const tab =
		selectedTab === "upvotes" && upvoteCount === 0 ? "requests" : selectedTab

	if (!project) {
		if (!isQuerySettled(projectResult)) return null
		return <ProjectDetailsEmptyScreen />
	}

	const ProjectIcon = getProjectIcon(project.icon)
	const projectIcon = (
		<ProjectIcon
			className="h-4 w-4 shrink-0"
			style={project.color ? { color: project.color } : undefined}
		/>
	)

	const hasOtherPortals = (workspace?.portalInstances ?? []).length >= 1

	return (
		<>
			{isMoveToPortalOpen && (
				<MoveRequestToPortalDialog
					isOpen
					onClose={() => setIsMoveToPortalOpen(false)}
					request={{
						type: "project",
						id: project.id,
						name: project.name,
						portalInstanceIds: project.portalInstanceIds ?? [],
					}}
				/>
			)}
			<SelectLinearIssuesDialog
				excludeStatus={["canceled"]}
				isOpen={isIssuesDialogOpen}
				onOpenChange={setIsIssuesDialogOpen}
				onSave={(issues) => {
					reportServerRejection(
						zero.mutate(
							mutators.issues.upsert({
								linearIssues: issues.map((issue) => ({
									...issue,
									dueDate: issue.dueDate ?? null,
									internalIssueId: generateId(),
									state: {
										...issue.state,
										name: issue.state.name || "Unknown",
										color: issue.state.color || "#000000",
									},
								})),
								internalUpdate: {
									isVisible: false,
									notUpdateVisibility: false,
								},
							}),
						),
						(message) => {
							toast.error({
								title: "Couldn't import these issues",
								description: message,
							})
						},
					)
				}}
				filterImportedIssues={true}
				title="Import issues"
				projectId={id}
			/>
			{!isDrawer && <MetaInfo title="Project Detail" />}
			<UpdateProjectDialog
				isOpen={!!updateProject}
				onOpenChange={(value) => {
					if (!value) {
						setUpdateProject(null)
						analytics.track("close-update-project-dialog", { id: project.id })
					}
				}}
				values={{
					id: project.id,
					color: project.color ?? "",
					icon: project.icon,
					name: project.name,
					status: project.state ?? "",
					teamId: project.linearTeamIds[0] ?? "",
					description: project.description ?? "",
				}}
			/>
			<DetailTopBar className="justify-between" clearsNavToggle={!isDrawer}>
				<div className="flex min-w-0 items-center gap-1">
					{isDrawer ? (
						<>
							<IconButton
								Icon={XIcon}
								variant="ghost"
								aria-label="Close"
								onClick={() => close()}
							/>
							<div className="flex min-w-0 items-center gap-1.5 px-1 text-sm font-medium text-primary">
								{projectIcon}
								<span className="truncate">{project.name}</span>
							</div>
						</>
					) : (
						<DetailBreadcrumb
							parentLabel={previousEntry?.label ?? "Projects"}
							onParentClick={() => close()}
							currentIcon={projectIcon}
							currentLabel={project.name}
						/>
					)}
					{hasPublicFeatureRequests && (
						<>
							<BreadcrumbSlash className="text-primary" />
							<TitleTabsSwitcher tab={titleTab} onTabChange={setTitleTab} />
						</>
					)}
					<DropdownMenu
						trigger={
							<IconButton
								Icon={MoreVertical}
								variant="ghost"
								iconClassName="fill-current"
							/>
						}
						contentProps={{ side: "bottom", align: "start" }}
					>
						<DropdownMenuGroup>
							{isDrawer && (
								<DropdownMenuItem
									Icon={ExpandIcon as LucideIcon}
									onClick={() =>
										void navigate({ href: detailPath("project", project.id) })
									}
								>
									Open full page
								</DropdownMenuItem>
							)}
							{hasOtherPortals && (
								<RoleCheck not={["VIEWER"]}>
									<DropdownMenuItem
										Icon={MoveToPortalIcon}
										onClick={() => setIsMoveToPortalOpen(true)}
									>
										Move to portal
									</DropdownMenuItem>
								</RoleCheck>
							)}
							{project.url && (
								<DropdownMenuItem
									Icon={LinearIcon as LucideIcon}
									shortcut="L"
									onClick={() => {
										analytics.track("open-project-in-linear", {
											id: project.id,
										})
										if (project.url) {
											window.open(project.url, "_blank")
										}
									}}
								>
									Open in Linear
								</DropdownMenuItem>
							)}
						</DropdownMenuGroup>
					</DropdownMenu>
				</div>
				<div className="flex-1" />
				<div className="flex items-center gap-1.5">
					{showsPortalVisibility(workspace) && (
						<VisibilityToggle
							type="project"
							id={project.id}
							isVisible={project.isVisible}
							portalInstanceIds={project.portalInstanceIds ?? []}
							variant="button"
							requests={project.feedbacks}
						/>
					)}

					{workspace?.domainPublic &&
						workspace.enabledLanguages &&
						workspace.enabledLanguages.length > 1 && (
							<LanguageSelector
								languages={workspace.enabledLanguages}
								selectedLanguage={selectedLanguage}
								onLanguageSelect={setSelectedLanguage}
								variant="code-only"
							/>
						)}

					<RoleCheck not={["VIEWER"]}>
						<NotifyRecipients
							entity={{
								id: project.id,
								name: project.name,
								type: "project",
							}}
						/>
					</RoleCheck>
					<SubscribeButton entityType="project" entityId={project.id} />
				</div>
			</DetailTopBar>
			<DetailScreenLayout>
				<DetailScreenEditor
					scrollKey={isDrawer ? `drawer:/projects/${id}` : undefined}
					contentClassName={isDrawer ? "h-auto min-h-full" : undefined}
				>
					<DescriptionSwitch
						item={project}
						publicDescription={project.publicDescription}
						publicTitle={project.publicTitle}
						id={project.id}
						type="project"
						tab={titleTab}
						selectedLanguage={editorLanguage}
					/>

					<div className="my-6 flex w-full items-center justify-center sm:px-6">
						<div className="w-full border-t" />
					</div>

					<Tabs
						value={tab}
						onValueChange={(value) => setTab(value as DetailsTabsType)}
					>
						<div className="my-4 flex min-h-7 w-full flex-row items-center justify-between px-5 sm:px-8">
							<TabsList className="w-fit justify-start">
								<TabsTrigger value="requests">Requests</TabsTrigger>
								<TabsTrigger value="issues">Issues</TabsTrigger>
								{upvoteCount > 0 && (
									<TabsTrigger value="upvotes" count={upvoteCount}>
										Upvotes
									</TabsTrigger>
								)}
							</TabsList>
							{tab === "requests" &&
								(threadsState.size > 0 ||
									hasActiveDetailFilters(threadControls.filters)) && (
									<DetailThreadsToolbar
										sortOrder={threadControls.sortOrder}
										onSortOrderChange={threadControls.setSortOrder}
										filters={threadControls.filters}
										onFiltersChange={threadControls.setFilters}
										teamMembers={teamMembers}
									/>
								)}
							{tab === "issues" && sortedProjectIssues.length > 0 && (
								<ImportIssuesButton
									variant="outline"
									size="sm"
									onClick={() => {
										setIsIssuesDialogOpen(true)
									}}
								/>
							)}
						</div>

						<TabsContent value="requests">
							<DetailThreadsList
								projectId={id}
								setThreadsSize={threadsState.report}
								controls={threadControls}
								hideLabel
							/>
							{threadsState.isEmpty && (
								<div className="flex min-h-[40dvh] w-full items-center justify-center py-8">
									<Empty
										text={
											"No customer requests are linked to this project yet. Wait for customers to upvote it or link it manually to relevant threads."
										}
										title="Customer requests"
										className="max-w-[20rem]"
										variant="left"
										image={
											<img src={EmptyIssue} alt="Empty customer requests" />
										}
										action={
											<div className="flex items-center gap-4">
												<Button
													onClick={() => {
														void navigate({ to: "/threads" })
													}}
													className="min-w-32"
												>
													Go to Inbox
												</Button>
												<HowItWorksButton
													className="min-h-7"
													article="7e861235-26a4-4b00-9268-a0e82258bbe6"
												/>
											</div>
										}
									/>
								</div>
							)}
						</TabsContent>

						<TabsContent value="issues">
							{sortedProjectIssues.length > 0 ||
							!isQuerySettled(projectResult) ? (
								<div className="mt-6">
									{sortedProjectIssues.map((issue) => (
										<ProjectOrIssueListItem
											key={issue.id}
											data={issue}
											type="issue"
											maxPainLevel={issuesMaxPainLevel}
											hidePriority={true}
										/>
									))}
								</div>
							) : (
								<div className="flex min-h-[40dvh] w-full items-center justify-center py-8">
									<Empty
										text="No project issues have been imported from Linear yet. Import them now to view and manage all your issues in one place."
										title="Project issues"
										className="max-w-[20rem]"
										variant="left"
										image={<img src={EmptyIssue} alt="Empty project issues" />}
										action={
											<ImportIssuesButton
												onClick={() => {
													setIsIssuesDialogOpen(true)
												}}
											/>
										}
									/>
								</div>
							)}
						</TabsContent>
						<TabsContent value="upvotes">
							<DetailThreadsList projectId={id} onlyUpvotes hideLabel />
							<DetailUpvotesList projectId={id} hideLabel />
						</TabsContent>
					</Tabs>
				</DetailScreenEditor>
			</DetailScreenLayout>
		</>
	)
}

const ProjectDetailsEmptyScreen = () => {
	return (
		<div className="flex w-full flex-1 items-center justify-center">
			<Empty
				text="It may have been moved or deleted, or you might not have access."
				title="Project not found"
				className="max-w-[20rem]"
				variant="center"
				image={<img src={EmptyIssue} alt="Project not found" />}
			/>
		</div>
	)
}

type LucideIcon = FC<SVGProps<SVGSVGElement>>
